import { useCallback, useState } from 'react';
import { addDays, subDays } from 'date-fns';

import { api } from '../api';
import { useResultStore } from '../store/resultStore';
import { checkDateRange, formatTime } from '../utils/time';
import { usePage } from './usePage';

const subLineLoadingState = { approve: false, reject: false, get: false, getSubLineInfo: false };

export const useSubLine = () => {
	const [loading, setLoading] = useState(subLineLoadingState);
	const [data, setData] = useState([]);
	const [request, setRequest] = useState({});
	const [searched, setSearched] = useState(false);
	const { currentPage, handlePage, pageSize, pageCount } = usePage(10);
	const open = useResultStore((state) => state.open);

	async function approve(requisitionId) {
		try {
			setLoading((value) => ({ ...value, approve: true }));
			await api.approveSubLineRequisition(requisitionId);
			await get(request);
			open({ type: 'success', text: 'Approve success !' });
		} catch (error) {
			open({ type: 'error', text: error?.response?.data?.message || error.message || 'Approve fail !' });
		} finally {
			setLoading((value) => ({ ...value, approve: false }));
		}
	}

	async function reject(requisitionId) {
		try {
			setLoading((value) => ({ ...value, reject: true }));
			await api.rejectSubLineRequisition(requisitionId);
			await get(request);
			open({ type: 'success', text: 'Reject success !' });
		} catch (error) {
			open({ type: 'error', text: error?.response?.data?.message || error.message || 'Reject fail !' });
		} finally {
			setLoading((value) => ({ ...value, reject: false }));
		}
	}

	async function get({ currentPage = 1, startTime, endTime }) {
		try {
			if (!startTime || !endTime) throw Error('Please enter date range');
			if (checkDateRange(startTime, endTime, 31)) throw Error('Date range have exceeded range');
			setLoading((value) => ({ ...value, get: true }));

			const { data } = await api.getSubLineRequisitions({
				currentPage,
				pageSize,
				startTime: formatTime(startTime),
				endTime: formatTime(endTime),
			});
			setRequest({ currentPage, pageSize, startTime, endTime });
			setSearched(true);
			if (!data.rowCount) {
				setData([]);
				return;
			}
			handlePage(data.currentPage, data.pageCount);
			setData(data.data ?? []);
		} catch (error) {
			setData([]);
			setSearched(false);
			open({ type: 'error', text: error?.response?.data?.message || error.message || 'Search fail !' });
		} finally {
			setLoading((value) => ({ ...value, get: false }));
		}
	}

	async function getSubLineInfo({ currentPage = 1, startTime, endTime }) {
		try {
			if (!startTime || !endTime) throw Error('Please enter date range');
			if (checkDateRange(startTime, endTime, 31)) throw Error('Date range have exceeded range');
			setLoading((value) => ({ ...value, getSubLineInfo: true }));
			const { data } = await api.getSubLineInfos({
				startTime: formatTime(startTime),
				endTime: formatTime(endTime),
				currentPage,
				pageSize,
			});
			if (!data.rowCount) throw Error('No result');
			handlePage(data.currentPage, data.pageCount);
			setData(data.data);
		} catch (error) {
			setData([]);
			open({ type: 'error', text: error?.response?.data?.message || error.message || 'Search fail !' });
		} finally {
			setLoading((value) => ({ ...value, getSubLineInfo: false }));
		}
	}

	const exist = useCallback(async (setStatus) => {
		try {
			const { data } = await api.getSubLineRequisitions({
				currentPage: 1,
				pageSize: 1,
				startTime: formatTime(subDays(new Date(), 30)),
				endTime: formatTime(addDays(new Date(), 1)),
			});
			setStatus(data.rowCount > 0);
		} catch (error) {
			console.log(error?.response?.data?.message || error.message || 'Check existence fail !');
		}
	}, []);

	return {
		loading,
		approve,
		reject,
		get,
		getSubLineInfo,
		exist,
		data,
		searched,
		currentPage,
		pageCount,
	};
};

export default useSubLine;
